import { useEffect, useState } from "react";
import { default as axios } from "../axiosConfig";
import MetricGroupCard from "./MetricGroupCard";
import Skeleton from "@mui/material/Skeleton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import GroupIcon from "@mui/icons-material/Group";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { API_GetFinancialUserSummary } from "../Assets/ApiUrlContainer";

function MetricCardSkeleton() {
  return (
    <div className="metric-card">
      <Skeleton variant="text" width={120} height={30} />
      <Skeleton variant="text" width={60} height={40} />
    </div>
  );
}

export default function FinancialControllerLandingPage({ user }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API_GetFinancialUserSummary}?email=${user.email}`);
        setSummary(res.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching Financial Controller summary:", err);
        setError("Failed to load dashboard data.");
      }
    };

    fetchSummary();
  }, [user.email]);

  const isLoading = !summary && !error;

  const financialData = summary?.financials ?? {};
  const managerData = summary?.manager ?? {};

  const chartData = [
    {
      name: "Total Value",
      amount: financialData.totalContractValue ?? 0
    },
    {
      name: "Contractor Cost",
      amount: financialData.totalContractorCost ?? 0
    },
    {
      name: "Avg Value",
      amount: financialData.averageContractValue ?? 0
    }
  ];

  return (
    <div className="landing-content">
      <div className="metric-panel">
        <div className="welcome-header">
          <h1>Welcome {user.firstname} {user.lastname}</h1>
        </div>

        {error && <div className="error-message">{error}</div>}

        {isLoading ? (
          <div className="metric-group">
            <div className="landing-grid">
              {[...Array(6)].map((_, i) => (
                <MetricCardSkeleton key={i} />
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Manager Section */}
            <div className="grid-two-columns">
              <MetricGroupCard
                className="group-card group-user"
                title={<><GroupIcon style={{ marginRight: "8px" }} />Consultant Overview</>}
                metrics={[
                  {
                    label: "Active Consultants",
                    value: managerData.activeConsultants ?? 0
                  },
                  {
                    label: "Applications This Week",
                    value: managerData.applicationsThisWeek ?? 0
                  }
                ]}
              />

              <MetricGroupCard
                className="group-card group-jobs"
                title={<><WorkOutlineIcon style={{ marginRight: "8px" }} />Job Activity</>}
                metrics={[
                  {
                    label: "New Jobs This Week",
                    value: managerData.newJobsThisWeek ?? 0
                  },
                  {
                    label: "Leave Requests",
                    value: managerData.leaveRequests ?? 0
                  },
                  {
                    label: "Timesheets to Approve",
                    value: managerData.timesheetsToApprove ?? 0
                  }
                ]}
              />
            </div>

            {/* Financial Section */}
            <div className="grid-two-columns">
              <MetricGroupCard
                className="group-card group-contracts"
                title={<><MonetizationOnIcon style={{ marginRight: "8px" }} />Financial Metrics</>}
                metrics={[
                  {
                    label: "Total Contract Value",
                    value: `$${financialData.totalContractValue?.toLocaleString() || 0}`
                  },
                  {
                    label: "Total Contractor Cost",
                    value: `$${financialData.totalContractorCost?.toLocaleString() || 0}`
                  },
                  {
                    label: "Avg Contract Value",
                    value: `$${financialData.averageContractValue?.toLocaleString() || 0}`
                  },
                  {
                    label: "Active Contracts",
                    value: financialData.activeContracts ?? 0
                  },
                  {
                    label: "Contracts Expiring",
                    value: financialData.contractsExpiringSoon ?? 0
                  }
                ]}
              />
            </div>

            {/* Financial Chart */}
            <div style={{ marginTop: "2rem", height: "300px", background: "#ffffff", padding: "1.5rem", borderRadius: "14px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
              <h3 style={{ 
                marginBottom: '1.25rem',
                marginTop: '0rem',
                color: '#2b2f38',
                fonSize: '1.2rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center', 
              }}>Financial Breakdown</h3>
              <ResponsiveContainer width="95%" height="95%">
                <BarChart data={chartData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="amount" fill="#4caf50" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
}